import { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { REACTION_KINDS, type ReactionKind } from '@durak/shared';
import { ReactionArt } from './ReactionArt';
import clsx from 'clsx';

const REACTION_LABEL: Record<ReactionKind, string> = {
  hi: 'Hi',
  bye: 'Bye',
  cry: 'Cry',
  laugh: 'Laugh',
  awesome: 'Awesome',
  gg: 'GG',
};

export function ReactionPicker({ onSend, className }: { onSend: (k: ReactionKind) => void; className?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={clsx('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="btn-ghost !py-1.5"
        aria-label="Reactions"
      >
        <span className="inline-flex items-center"><ReactionArt kind="laugh" size={22} /></span>
        <span className="hidden sm:inline">React</span>
      </button>
      {/* Portal: the footer panel's backdrop-filter makes it the containing
          block for fixed-position descendants, so an in-place `inset-0` scrim
          would only cover the footer strip — taps on the table would slip
          through to the cards while the picker stays open. */}
      {createPortal(
      <AnimatePresence>
        {open && (
          <>
            <button
              tabIndex={-1}
              className="fixed inset-0 z-40 cursor-default bg-ink-950/40"
              onClick={() => setOpen(false)}
              aria-hidden
            />
            {/* Wrapper handles fixed positioning so framer-motion's animated
                transform on the inner panel doesn't fight with -translate-x-1/2 */}
            <div
              className="fixed left-0 right-0 z-50 flex justify-center pointer-events-none"
              style={{ bottom: 'calc(env(safe-area-inset-bottom) + 84px)' }}
            >
              <motion.div
                initial={{ opacity: 0, y: 16, scale: 0.94 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 16, scale: 0.94 }}
                transition={{ type: 'spring', stiffness: 360, damping: 26 }}
                className="panel !p-2 flex gap-1 pointer-events-auto"
              >
                {REACTION_KINDS.map(k => (
                  <button
                    key={k}
                    type="button"
                    className="flex flex-col items-center gap-0.5 rounded-lg px-2 py-1.5 hover:bg-ink-700/70 active:scale-95 transition"
                    onClick={() => { onSend(k); setOpen(false); }}
                    aria-label={REACTION_LABEL[k]}
                  >
                    <ReactionArt kind={k} size={40} />
                    <span className="text-[10px] text-ink-200">{REACTION_LABEL[k]}</span>
                  </button>
                ))}
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>,
      document.body,
      )}
    </div>
  );
}

/** Floating reaction bubble — animated art rises from below the seat and fades. */
export function ReactionBubble({ kind, id }: { kind: ReactionKind; id: number }) {
  return (
    <motion.div
      key={id}
      initial={{ opacity: 0, y: 16, scale: 0.5 }}
      animate={{ opacity: 1, y: -14, scale: 1 }}
      exit={{ opacity: 0, y: -44, scale: 0.9 }}
      transition={{ type: 'spring', stiffness: 280, damping: 18 }}
      className="absolute -top-4 -right-3 z-10 pointer-events-none select-none"
      style={{ filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.45))' }}
    >
      <ReactionArt kind={kind} size={44} />
    </motion.div>
  );
}
